import {
  ARABIC_LETTER_MARK,
  FIRST_STRONG_ISOLATE,
  LEFT_TO_RIGHT_MARK,
  LTR_REGEX,
  POP_DIRECTIONAL_ISOLATE,
  RIGHT_TO_LEFT_MARK,
  RTL_BLOCK_REGEX,
  RTL_REGEX,
} from "../constants/direction";
import type { IDirectionProps, TAutoDirection } from "../types/direction";
import { EDirection } from "../types/direction";

export const directionFromString = (dir: string): EDirection => {
  return dir === EDirection.Rtl ? EDirection.Rtl : EDirection.Ltr;
};

const firstStrongDirection = (value: string): EDirection | null => {
  for (const char of value) {
    if (RTL_REGEX.test(char)) return EDirection.Rtl;
    if (LTR_REGEX.test(char)) return EDirection.Ltr;
  }

  return null;
};

export const direction = ({ value, fallback }: IDirectionProps): EDirection => {
  return firstStrongDirection(String(value ?? "")) ?? fallback;
};

// NOTE: NSAI-6726 — the first strong character of one line AS THE BROWSER READS
// IT, because the explicit `dir` this feeds must never disagree with what
// `dir="auto"` would have resolved:
// - bidi marks are strong: `\u200Eمرحبا` is LTR to the textarea;
// - only LETTERS are otherwise strong: Arabic-Indic digits and Arabic
//   punctuation (`١٢٣`, `،`) share the Arabic block but are weak / neutral, so a
//   digits-only line does not vote;
// - a letter is RTL when it sits in a right-to-left block, supplementary planes
//   included, so Adlam or Syriac never falls through to an explicit `ltr`.
// Kept out of `direction()`, whose callers (NSAI-5958 on mobile) rely on its
// current letters-and-Arabic-block reading.
const firstStrongLineDirection = (line: string): EDirection | null => {
  for (const char of line) {
    if (char === RIGHT_TO_LEFT_MARK || char === ARABIC_LETTER_MARK) {
      return EDirection.Rtl;
    }
    if (char === LEFT_TO_RIGHT_MARK) return EDirection.Ltr;
    if (!LTR_REGEX.test(char)) continue;
    return RTL_BLOCK_REGEX.test(char) ? EDirection.Rtl : EDirection.Ltr;
  }

  return null;
};

// NOTE: NSAI-6726 — the `dir` a multi-line text field should carry for its
// draft. `dir="auto"` on a <textarea> is not the same as on a <div>: the UA
// stylesheet adds `unicode-bidi: plaintext`, which resolves every LINE on its
// own, and a line with no strong character — the empty line right after
// Shift+Enter — resolves to LTR whatever the field's direction is. That put the
// caret at the left edge of every new line of an Arabic draft until the first
// letter flipped it back.
//
// An explicit `dir` has no such hole: an empty line takes the field's
// direction. So a draft whose lines all read one way gets that direction
// explicitly. Only a draft that genuinely mixes directions line by line keeps
// "auto", because there the per-line resolution is what lets an English line
// in an Arabic draft stay left-aligned — and nothing else offers it.
//
// Lines with no strong character (blank, digits of any script, punctuation) do
// not vote; a draft with no strong character at all falls back, as `direction`
// does. A bidi mark (LRM / RLM / ALM) counts as the strong character it is.
export const draftDirection = ({
  value,
  fallback,
}: IDirectionProps): TAutoDirection => {
  const lineDirections = new Set<EDirection>();

  for (const line of String(value ?? "").split("\n")) {
    const lineDirection = firstStrongLineDirection(line);
    if (!lineDirection) continue;
    lineDirections.add(lineDirection);
    if (lineDirections.size > 1) return "auto";
  }

  const [onlyDirection] = lineDirections;
  return onlyDirection ?? fallback;
};

// NOTE: NSAI-4800 — wraps one interpolated value in bidi isolates so its
// direction is resolved on its own instead of being inherited from the sentence
// it lands in.
//
// Without this, a translated template that embeds a value of the opposite
// direction produces a single paragraph whose base direction comes from the
// FIRST strong character — the template's. An Arabic label carrying an English
// server message (`آخر خطأ: The AI model could not complete this run. Please try
// again later.`) is therefore laid out right-to-left as a whole, and the
// English sentence's final period — bidi-neutral, so it takes the paragraph's
// direction rather than its neighbour's — is painted at the LEFT end of the
// wrapped line: `.Please try again later`.
//
// Isolating the value fixes that without touching either language's own
// behaviour: the Arabic template still reads right-to-left, and the value reads
// whichever way its own content says.
//
// Empty and whitespace-only values are returned untouched — there is no
// direction to isolate, and a bare pair of isolates in the output would only
// inflate the string.
export const isolateDirection = (value: string): string => {
  if (!value.trim()) return value;
  return `${FIRST_STRONG_ISOLATE}${value}${POP_DIRECTIONAL_ISOLATE}`;
};
